import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import leavePolicyApi, { CarryoverRun } from '../../../network/leavePolicy';
import { useToast } from '../../../utils/useToast';
import { Button } from '../../../ui/Button';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function errorMessage(error: any, fallback: string) {
  const message = error?.response?.data?.message;
  return typeof message === 'string' && message ? message : fallback;
}

/**
 * Settings > Carryover: how much unused paid holiday moves into the next
 * year, when it lapses, and running or previewing it for a year. It is off
 * until an admin turns it on or runs it.
 */
export function CarryoverSettings() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const settingsQuery = useQuery(
    ['leave-settings'],
    () => leavePolicyApi.getLeaveSettings(),
    { retry: false },
  );

  const [enabled, setEnabled] = useState(false);
  const [capDays, setCapDays] = useState('5');
  const [hasExpiry, setHasExpiry] = useState(false);
  const [expiryMonth, setExpiryMonth] = useState(3);
  const [expiryDay, setExpiryDay] = useState('31');
  const [year, setYear] = useState(new Date().getFullYear());
  const [preview, setPreview] = useState<CarryoverRun | null>(null);

  useEffect(() => {
    const carryover = settingsQuery.data?.carryover;
    if (!carryover) return;
    setEnabled(carryover.enabled);
    setCapDays(String(carryover.cap_days));
    setHasExpiry(Boolean(carryover.expiry));
    if (carryover.expiry) {
      setExpiryMonth(carryover.expiry.month);
      setExpiryDay(String(carryover.expiry.day));
    }
  }, [settingsQuery.data]);

  const save = useMutation(
    () =>
      leavePolicyApi.updateLeaveSettings({
        carryover: {
          enabled,
          cap_days: Number(capDays),
          expiry: hasExpiry
            ? { month: expiryMonth, day: Number(expiryDay) }
            : null,
        },
      }),
    {
      onSuccess: () => {
        toast({ title: 'Carryover settings saved', variant: 'success' });
        queryClient.invalidateQueries('leave-settings');
      },
      onError: (error) =>
        void toast({
          title: 'Carryover settings not saved',
          description: errorMessage(error, 'Please try again.'),
          variant: 'error',
        }),
    },
  );

  const previewRun = useMutation(() => leavePolicyApi.previewCarryover(year), {
    onSuccess: (result) => setPreview(result),
    onError: (error) =>
      void toast({
        title: 'Preview failed',
        description: errorMessage(error, 'Please try again.'),
        variant: 'error',
      }),
  });

  const run = useMutation(() => leavePolicyApi.runCarryover(year), {
    onSuccess: (result) => {
      setPreview(result);
      toast({
        title: `Carryover from ${year} saved`,
        description: `${result?.lines?.length ?? 0} people updated. Running it again replaces these amounts.`,
        variant: 'success',
      });
    },
    onError: (error) =>
      void toast({
        title: 'Carryover not run',
        description: errorMessage(error, 'Please try again.'),
        variant: 'error',
      }),
  });

  const thisYear = new Date().getFullYear();

  return (
    <div className="flex flex-col gap-8">
      <form
        aria-label="Carryover policy"
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Carry unused paid holiday into the next year automatically on 1 January
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Maximum days carried over
          <input
            type="number"
            min={0}
            max={365}
            step={0.5}
            className="w-32 rounded-md border border-gray-300 px-3 py-2"
            value={capDays}
            onChange={(e) => setCapDays(e.target.value)}
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={hasExpiry}
            onChange={(e) => setHasExpiry(e.target.checked)}
          />
          Carried days lapse if not used by
        </label>
        {hasExpiry && (
          <div className="flex gap-2">
            <input
              aria-label="Expiry day"
              type="number"
              min={1}
              max={31}
              className="w-20 rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={expiryDay}
              onChange={(e) => setExpiryDay(e.target.value)}
            />
            <select
              aria-label="Expiry month"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={expiryMonth}
              onChange={(e) => setExpiryMonth(Number(e.target.value))}
            >
              {MONTHS.map((name, index) => (
                <option key={name} value={index + 1}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <Button type="submit" color="red" disabled={save.isLoading}>
            Save carryover settings
          </Button>
        </div>
      </form>

      <section className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold">Run carryover</h3>
        <p className="text-sm text-gray-500">
          Works out each person&apos;s unused paid holiday for the year and
          carries it into the next, up to the maximum. Safe to run again: it
          replaces the amounts rather than adding to them.
        </p>
        <label className="flex items-center gap-2 text-sm">
          From year
          <select
            aria-label="Carryover year"
            className="rounded-md border border-gray-300 px-2 py-1"
            value={year}
            onChange={(e) => {
              setYear(Number(e.target.value));
              setPreview(null);
            }}
          >
            {[thisYear - 1, thisYear].map((y) => (
              <option key={y} value={y}>
                {y} into {y + 1}
              </option>
            ))}
          </select>
        </label>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={previewRun.isLoading}
            onClick={() => previewRun.mutate()}
          >
            Preview
          </Button>
          <Button
            color="red"
            disabled={run.isLoading}
            onClick={() => {
              if (
                window.confirm(
                  `Carry unused ${year} paid holiday into ${year + 1} for everyone now?`,
                )
              ) {
                run.mutate();
              }
            }}
          >
            Run now
          </Button>
        </div>

        {preview && (
          <div className="overflow-x-auto">
            <p className="mb-2 text-sm text-gray-600">
              {preview.dry_run ? 'Preview' : 'Saved'}: {preview.from_year} into{' '}
              {preview.to_year}, up to {preview.cap_days} days
              {preview.expires_at
                ? `, lapsing ${new Date(preview.expires_at).toLocaleDateString('en-GB')}`
                : ''}
              .
            </p>
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-2 py-1">Person</th>
                  <th className="px-2 py-1">Allowance</th>
                  <th className="px-2 py-1">Used</th>
                  <th className="px-2 py-1">Unused</th>
                  <th className="px-2 py-1">Carried over</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {preview.lines.map((line) => (
                  <tr key={line.user}>
                    <td className="px-2 py-1">{line.name || line.user}</td>
                    <td className="px-2 py-1">
                      {line.allowed + line.carried_in}
                    </td>
                    <td className="px-2 py-1">{line.used}</td>
                    <td className="px-2 py-1">{line.unused}</td>
                    <td className="px-2 py-1 font-medium">{line.carried_over}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
